import type { MatchEngine } from "../game/match";
import {
  MAX_CURRENCY,
  MAX_ELIMINATIONS,
  MAX_FOOD,
  MAX_MATCHES,
  MAX_SCORE,
  MAX_TOTAL,
  type Progress,
} from "../progress/schema";
import type { MatchResult } from "./types";

export const deathCopy: Record<MatchResult["deathReason"], string> = {
  boundary: "越过了暮界",
  body: "撞上了危险线迹",
  "head-to-head": "在正面相遇中同时熄灭",
  time: "完成了全部三分钟航程",
  exit: "主动结束了本次无尽航程",
};

export const capAdd = (value: number, added: number, maximum: number): number =>
  Math.min(maximum, value + Math.max(0, Math.round(added)));

export const resultFromEngine = (
  engine: MatchEngine,
  forcedReason?: MatchResult["deathReason"],
): MatchResult => {
  const current = engine.snapshot;
  const player = current.snakes.find((snake) => snake.id === "player");
  const playerDeath = [...current.deaths]
    .reverse()
    .find((death) => death.snakeId === "player");
  const result = engine.resultFor("player");
  return {
    score: result?.score ?? 0,
    maxLength: Math.round(result?.maxLength ?? player?.length ?? 0),
    foodCollected: result?.foodCollected ?? 0,
    survivalMs:
      result?.survivalMs ??
      Math.round((playerDeath?.atSeconds ?? current.elapsedSeconds) * 1_000),
    eliminations: result?.eliminations ?? 0,
    finalRank: result?.finalRank ?? 1,
    deathReason:
      forcedReason ??
      playerDeath?.reason ??
      (current.phase === "ended" ? "time" : "exit"),
    mode: result?.mode ?? current.mode ?? "signal",
    assisted: result?.assisted ?? current.assisted ?? false,
    deaths: result?.deaths ?? 0,
    sessionMs: result?.sessionMs ?? Math.round(current.elapsedSeconds * 1_000),
    aliveMs: result?.aliveMs ?? result?.survivalMs ?? 0,
    currentLength: Math.round(result?.currentLength ?? player?.length ?? 160),
  };
};

export const applyMatchResult = (
  draft: Progress,
  result: MatchResult,
): void => {
  const records = draft.records;
  if (result.mode === "endless") {
    records.endlessSessions = capAdd(records.endlessSessions, 1, MAX_MATCHES);
    records.endlessDeaths = capAdd(
      records.endlessDeaths,
      result.deaths,
      MAX_ELIMINATIONS,
    );
    records.longestEndlessSessionMs = Math.max(
      records.longestEndlessSessionMs,
      result.sessionMs,
    );
    records.longestEndlessLength = Math.max(
      records.longestEndlessLength,
      result.maxLength,
    );
    records.endlessEliminations = capAdd(
      records.endlessEliminations,
      result.eliminations,
      MAX_ELIMINATIONS,
    );
    records.endlessFoodCollected = capAdd(
      records.endlessFoodCollected,
      result.foodCollected,
      MAX_FOOD,
    );
    records.totalEndlessSessionMs = capAdd(
      records.totalEndlessSessionMs,
      result.sessionMs,
      MAX_TOTAL,
    );
    records.totalEndlessAliveMs = capAdd(
      records.totalEndlessAliveMs,
      result.aliveMs,
      MAX_TOTAL,
    );
    return;
  }

  records.bestScore = Math.min(
    MAX_SCORE,
    Math.max(records.bestScore, result.score),
  );
  records.longestLength = Math.max(
    records.longestLength,
    Math.max(0, Math.round(result.maxLength)),
  );
  records.longestSurvivalMs = Math.max(
    records.longestSurvivalMs,
    Math.max(0, Math.round(result.survivalMs)),
  );
  records.matchesPlayed = capAdd(records.matchesPlayed, 1, MAX_MATCHES);
  records.eliminations = capAdd(
    records.eliminations,
    result.eliminations,
    MAX_ELIMINATIONS,
  );
  records.foodCollected = capAdd(
    records.foodCollected,
    result.foodCollected,
    MAX_FOOD,
  );
  records.totalScore = capAdd(records.totalScore, result.score, MAX_TOTAL);
  records.totalSurvivalMs = capAdd(
    records.totalSurvivalMs,
    result.survivalMs,
    MAX_TOTAL,
  );
  draft.currency = capAdd(
    draft.currency,
    Math.floor(result.score / 25),
    MAX_CURRENCY,
  );
  draft.tutorial = { completed: true, step: 5 };
  draft.ownedCosmetics.coral = true;
  if (result.survivalMs >= 90_000) draft.ownedCosmetics.lime = true;
  if (result.eliminations > 0) draft.ownedCosmetics.constellation = true;
  if (records.bestScore >= 750) draft.ownedCosmetics["indigo-tide"] = true;
  if (records.eliminations >= 3) draft.ownedCosmetics["jade-circuit"] = true;
};
